import React, { useCallback, useEffect, useRef, useState } from "react";
import { Alert, Box, Button, Card, CardContent, Chip, CircularProgress, IconButton, Stack, TextField, Tooltip, Typography } from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import RefreshIcon from "@mui/icons-material/Refresh";
import { apiManualesSupportList, apiManualSupportDetalle, apiManualSupportGuardar, apiManualSupportActualizar, apiManualSupportEliminar, apiManualSupportImagen, apiManualSupportPublicar, apiManualSupportConocimientoIniciar, apiManualSupportConocimientoEstado, apiManualSupportCerrarConversacion } from "../settings/supportApi";
import { deleteManualDraft, listManualDrafts, saveManualDraft } from "../settings/manualDraftStore";
import { supportDebugLog } from "../settings/n8nWebhookApi";
import AgentKnowledgeTestChat from "../components/AgentKnowledgeTestChat";

const MAX_IMAGE_BYTES = 4 * 1024 * 1024;
const MAX_IMAGES = 10;
const ALLOWED_IMAGE_TYPES = ["image/png", "image/jpeg", "image/jpg", "image/pjpeg", "image/webp", "image/gif"];
// Mantiene margen bajo el límite de 1 MiB que suelen aplicar los proxies de entrada.
const MAX_N8N_PAYLOAD_BYTES = 24 * 1024 * 1024;
const IMAGE_OPTIMIZATION_LEVELS = [
  // Primero conserva las dimensiones originales y reduce el peso con WebP de alta calidad.
  { maxDimension: Number.POSITIVE_INFINITY, quality: 0.96 },
  { maxDimension: Number.POSITIVE_INFINITY, quality: 0.92 },
  { maxDimension: Number.POSITIVE_INFINITY, quality: 0.88 },
  { maxDimension: Number.POSITIVE_INFINITY, quality: 0.84 },
  // Solo si hace falta, reduce resolución gradualmente y conserva un mínimo legible.
  { maxDimension: 3200, quality: 0.90 },
  { maxDimension: 2880, quality: 0.90 },
  { maxDimension: 2560, quality: 0.88 },
  { maxDimension: 2240, quality: 0.86 },
  { maxDimension: 1920, quality: 0.84 },
];
const empty = { slug: "", title: "", topic: "", version: "1.0", content_markdown: "" };

const errorMessage = (error, fallback) =>
  error.response ? fallback : (error.message || fallback);

const saveFailureMessage = (error) => {
  const status = error.httpCode || error.response?.status;
  if (status === 401 || status === 403) {
    return "No se pudo guardar el manual. Revisa tu sesión y vuelve a intentarlo. Se conservó una copia en este navegador.";
  }
  if (status === 413) {
    return "El manual o sus imágenes superan el tamaño permitido. Reduce el tamaño o la cantidad de imágenes y vuelve a intentarlo. Se conservó una copia en este navegador.";
  }
  return "No se pudo confirmar el guardado. Se conservó una copia en este navegador; revisal catálogo antes de volver a intentarlo.";
};

const readFile = (file, caption) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = () => resolve({
    file_name: file.name,
    media_type: (file.type === "image/jpg" || file.type === "image/pjpeg") ? "image/jpeg" : file.type,
    data_base64: String(reader.result).split(",")[1],
    caption: caption || "",
  });
  reader.onerror = () => reject(new Error(`No se pudo leer ${file.name}.`));
  reader.readAsDataURL(file);
});

const optimizarImagen = async (file, level) => {
  // No convertir GIF: canvas conservaría solo el primer cuadro y perdería la animación.
  if (file.type === "image/gif" || level.quality >= 1 || !window.createImageBitmap) return file;

  let bitmap;
  try {
    bitmap = await window.createImageBitmap(file);
  } catch {
    return file;
  }

  try {
    const scale = Math.min(1, level.maxDimension / Math.max(bitmap.width, bitmap.height));
    const canvas = document.createElement("canvas");
    canvas.width = Math.max(1, Math.round(bitmap.width * scale));
    canvas.height = Math.max(1, Math.round(bitmap.height * scale));
    const context = canvas.getContext("2d", { alpha: true });
    if (!context) return file;
    context.drawImage(bitmap, 0, 0, canvas.width, canvas.height);

    const blob = await new Promise(resolve => canvas.toBlob(resolve, "image/webp", level.quality));
    canvas.width = 0;
    canvas.height = 0;
    if (!blob || blob.size >= file.size) return file;

    const baseName = file.name.replace(/\.[^.]+$/, "") || "imagen";
    return new File([blob], `${baseName}.webp`, { type: "image/webp", lastModified: file.lastModified });
  } catch {
    return file;
  } finally {
    bitmap.close?.();
  }
};

const prepararImagenesParaN8n = async (files, captions, form, updating, protectedCount = 0) => {
  const construirPayload = async candidateFiles => {
    const images = await Promise.all(candidateFiles.map((file, index) => readFile(file, captions[index])));
    const payload = { ...form, ...(images.length ? { images } : {}), modified_by: "Support" };
    if (!updating && !images.length) payload.images = [];
    const bytes = new Blob([JSON.stringify(payload)]).size;
    supportDebugLog("Imágenes: tamaño del payload preparado", {
      updating,
      payloadBytes: bytes,
      limitBytes: MAX_N8N_PAYLOAD_BYTES,
      images: images.map(image => ({
        name: image.file_name,
        mediaType: image.media_type,
        base64Characters: image.data_base64?.length || 0,
      })),
    });
    return { images, bytes };
  };

  let result = await construirPayload(files);
  if (result.bytes <= MAX_N8N_PAYLOAD_BYTES) return result.images;

  for (const level of IMAGE_OPTIMIZATION_LEVELS) {
    const optimizedFiles = [];
    for (let index = 0; index < files.length; index += 1) {
      optimizedFiles.push(index < protectedCount ? files[index] : await optimizarImagen(files[index], level));
    }
    result = await construirPayload(optimizedFiles);
    if (result.bytes <= MAX_N8N_PAYLOAD_BYTES) return result.images;
  }

  throw new Error("Las imágenes siguen superando el límite permitido después de optimizarlas. El respaldo local conserva las originales; reduce la cantidad de imágenes o el tamaño de los GIF y vuelve a guardar.");
};

const newDraftId = () => window.crypto?.randomUUID?.() || `draft-${Date.now()}-${Math.random().toString(16).slice(2)}`;

export default function ManualesAgentePage() {
  const formRef = useRef(null);
  const [form, setForm] = useState(empty);
  const [files, setFiles] = useState([]);
  const [captions, setCaptions] = useState([]);
  const [selectedPreviews, setSelectedPreviews] = useState([]);
  const [storedImages, setStoredImages] = useState([]);
  const [storedImageCount, setStoredImageCount] = useState(0);
  const [storedImagesLoading, setStoredImagesLoading] = useState(false);
  const [storedImagesError, setStoredImagesError] = useState(false);
  const [localDraftId, setLocalDraftId] = useState(null);
  const [manualId, setManualId] = useState(null);
  const [localDrafts, setLocalDrafts] = useState([]);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [draftsLoading, setDraftsLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [fileInputKey, setFileInputKey] = useState(0);
  const [notice, setNotice] = useState(null);
  const [catalogError, setCatalogError] = useState("");
  const [testTarget, setTestTarget] = useState(null);
  const [testPreparing, setTestPreparing] = useState(false);
  const [preparandoImagenes, setPreparandoImagenes] = useState(false);
  const testPreviewUrls = useRef([]);
  const storedImagesRequest = useRef(0);

  useEffect(() => () => {
    storedImages.forEach(image => image.url && URL.revokeObjectURL(image.url));
  }, [storedImages]);

  useEffect(() => () => { storedImagesRequest.current += 1; }, []);

  useEffect(() => {
    const previews = files.map(file => ({ name: file.name, size: file.size, url: URL.createObjectURL(file) }));
    setSelectedPreviews(previews);
    return () => previews.forEach(preview => URL.revokeObjectURL(preview.url));
  }, [files]);

  useEffect(() => () => {
    items.forEach(item => (item.imagePreviews || []).forEach(preview => preview && URL.revokeObjectURL(preview)));
  }, [items]);

  useEffect(() => () => {
    testPreviewUrls.current.forEach(preview => preview && URL.revokeObjectURL(preview));
  }, []);

  const cargarCapturasLocales = useCallback(async () => {
    setDraftsLoading(true);
    try {
      setLocalDrafts(await listManualDrafts());
    } catch (error) {
      setNotice({ type: "error", text: errorMessage(error, "No se pudieron leer las capturas locales de este navegador.") });
    } finally {
      setDraftsLoading(false);
    }
  }, []);

  const cargarCatalogo = useCallback(async () => {
    setLoading(true);
    setCatalogError("");
    try {
      const data = await apiManualesSupportList();
      const manuals = Array.isArray(data?.items) ? data.items : [];
      const withPreviews = await Promise.all(manuals.map(async item => {
        // Tolera workflows sin image_metadata: usa image_count o lista vacía.
        const metadata = item.image_metadata || item.imagenes || [];
        const entries = metadata
          .map(entry => (typeof entry === "object" ? entry?.image_id : entry))
          .filter(value => value !== undefined && value !== null);
        // Si no hay metadata pero hay conteo, no se inventan IDs: se omite preview.
        const imagePreviews = await Promise.all(entries.map(imageId =>
          apiManualSupportImagen(item.manual_id, imageId).catch(() => null)
        ));
        return { ...item, imagePreviews };
      }));
      setItems(withPreviews);
    } catch (error) {
      setCatalogError(`${errorMessage(error, "No se pudo conectar para consultar el catálogo.")} La lista mostrada puede no estar actualizada.`);
    } finally {
      setLoading(false);
    }
  }, []);

  const abrirPruebaConocimiento = async (manualOrId) => {
    const id = typeof manualOrId === "object" ? manualOrId.manual_id : manualOrId;
    if (!id) return;
    setTestPreparing(true);
    setNotice(null);
    try {
      let detail;
      try {
        detail = await apiManualSupportDetalle(id);
      } catch (error) {
        if (error.httpCode !== 404) throw error;
        const catalogo = await apiManualesSupportList();
        detail = catalogo.items.find(item => String(item.manual_id) === String(id));
        if (!detail) throw error;
        setNotice({ type: "warning", text: "No se pudo cargar el detalle completo. La prueba usa la información disponible en el catálogo." });
      }
      const metadata = detail.image_metadata || detail.imagenes || [];
      const entries = metadata
        .map(entry => (typeof entry === "object" ? entry : { image_id: entry }))
        .filter(entry => entry?.image_id !== undefined && entry?.image_id !== null);
      const urls = await Promise.all(entries.map(image =>
        apiManualSupportImagen(id, image.image_id).catch(() => null)
      ));
      testPreviewUrls.current.forEach(preview => preview && URL.revokeObjectURL(preview));
      testPreviewUrls.current = urls.filter(Boolean);
      setTestTarget({
        manual: detail,
        sessionKey: newDraftId(),
        images: entries.map((image, index) => ({ ...image, url: urls[index] })),
      });
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (error) {
      setNotice({ type: "error", text: errorMessage(error, "No se pudo preparar la prueba de conocimiento.") });
    } finally {
      setTestPreparing(false);
    }
  };

  const cerrarPruebaConocimiento = () => {
    testPreviewUrls.current.forEach(preview => preview && URL.revokeObjectURL(preview));
    testPreviewUrls.current = [];
    setTestTarget(null);
  };

  useEffect(() => {
    cargarCapturasLocales();
    cargarCatalogo();
  }, [cargarCapturasLocales, cargarCatalogo]);

  const limpiarImagenesGuardadas = () => {
    storedImagesRequest.current += 1;
    setStoredImages([]);
    setStoredImageCount(0);
    setStoredImagesLoading(false);
    setStoredImagesError(false);
  };

  const cargarImagenesGuardadas = async (detail) => {
    const request = ++storedImagesRequest.current;
    const metadata = detail.image_metadata || detail.imagenes || [];
    const entries = Array.isArray(metadata) ? metadata
      .map(image => (typeof image === "object" && image !== null ? image : { image_id: image }))
      .filter(image => image.image_id !== undefined && image.image_id !== null) : [];
    setStoredImages([]);
    setStoredImageCount(Math.max(Number(detail.image_count) || 0, entries.length));
    setStoredImagesError(false);
    setStoredImagesLoading(Boolean(entries.length));
    const results = await Promise.all(entries.map(async image => ({
      ...image,
      url: await apiManualSupportImagen(detail.manual_id, image.image_id).catch(() => null),
    })));
    if (request !== storedImagesRequest.current) {
      results.forEach(image => image.url && URL.revokeObjectURL(image.url));
      return;
    }
    setStoredImages(results);
    setStoredImagesLoading(false);
  };

  const limpiarEditor = () => {
    limpiarImagenesGuardadas();
    testPreviewUrls.current.forEach(preview => preview && URL.revokeObjectURL(preview));
    testPreviewUrls.current = [];
    setTestTarget(null);
    setForm(empty);
    setFiles([]);
    setCaptions([]);
    setLocalDraftId(null);
    setManualId(null);
    setFileInputKey(value => value + 1);
  };

  const validarEditor = () => {
    if (!formRef.current?.reportValidity()) return false;
    if (!/^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(form.slug)) {
      setNotice({ type: "error", text: "El slug debe usar minúsculas, números y guiones entre palabras." });
      return false;
    }
    return true;
  };

  const persistirCapturaLocal = async () => {
    const now = new Date().toISOString();
    const existing = localDrafts.find(draft => draft.draft_id === localDraftId);
    const draft = {
      draft_id: localDraftId || newDraftId(),
      created_at: existing?.created_at || now,
      updated_at: now,
      manual_id: manualId || existing?.manual_id || null,
      form: { ...form },
      images: files.map((file, index) => ({
        file_name: file.name,
        media_type: (file.type === "image/jpg" || file.type === "image/pjpeg") ? "image/jpeg" : file.type,
        blob: file,
        caption: captions[index] || "",
      })),
    };
    supportDebugLog("Respaldo local: guardando captura", {
      draftId: draft.draft_id,
      manualId: draft.manual_id,
      title: draft.form.title,
      slug: draft.form.slug,
      contentCharacters: draft.form.content_markdown.length,
      images: draft.images.map(image => ({
        name: image.file_name,
        mediaType: image.media_type,
        bytes: image.blob.size,
      })),
    });
    await saveManualDraft(draft);
    setLocalDraftId(draft.draft_id);
    await cargarCapturasLocales();
    supportDebugLog("Respaldo local: captura guardada", { draftId: draft.draft_id });
    return draft;
  };

  const guardarCapturaLocal = async () => {
    if (!validarEditor()) return;
    setBusy(true);
    setNotice(null);
    try {
      await persistirCapturaLocal();
      setNotice({ type: "success", text: "Captura guardada en este navegador. Podrás enviarla al catálogo cuando el servicio esté disponible." });
    } catch (error) {
      setNotice({ type: "error", text: errorMessage(error, "No se pudo guardar la captura local.") });
    } finally {
      setBusy(false);
    }
  };

  const guardarEnCatalogo = async (event) => {
    event.preventDefault();
    if (!validarEditor()) return;
    setBusy(true);
    setNotice(null);
    try {
      const wasUpdating = Boolean(manualId);
      supportDebugLog("Editor: inicio de guardado", {
        operation: wasUpdating ? "actualizar" : "crear",
        manualId,
        localDraftId,
        form,
        selectedImages: files.map((file, index) => ({
          name: file.name,
          mediaType: file.type,
          bytes: file.size,
          caption: captions[index] || "",
        })),
      });
      setPreparandoImagenes(Boolean(files.length));
      let priorFiles = [];
      if (wasUpdating && files.length) {
        if (storedImagesLoading || storedImagesError || storedImages.length !== storedImageCount || storedImages.some(image => !image.url)) {
          throw new Error("No se pudieron recuperar todas las imágenes guardadas. Ábrelas de nuevo antes de agregar otras para evitar perderlas.");
        }
        priorFiles = await Promise.all(storedImages.map(async (image, index) => {
          const response = await fetch(image.url);
          if (!response.ok) throw new Error("No se pudo leer una imagen guardada. Vuelve a abrir el manual e inténtalo de nuevo.");
          const blob = await response.blob();
          return new File([blob], image.file_name || `imagen-${index + 1}`, { type: image.mime_type || blob.type });
        }));
      }
      const images = files.length
        ? await prepararImagenesParaN8n([...priorFiles, ...files], [...storedImages.map(image => image.caption || ""), ...captions], form, wasUpdating, priorFiles.length)
        : undefined;
      setPreparandoImagenes(false);
      let savedManual = null;
      try {
        const payload = {
          ...form,
          ...(images !== undefined ? { images } : {}),
          modified_by: "Support",
          ...(manualId ? { manual_id: manualId } : {}),
        };
        supportDebugLog("Editor: payload listo para n8n", {
          operation: wasUpdating ? "actualizar" : "crear",
          manualId,
          payload,
          imageBytes: (images || []).map(image => ({
            name: image.file_name,
            mediaType: image.media_type,
            base64Characters: image.data_base64?.length || 0,
          })),
        });
        if (manualId) savedManual = await apiManualSupportActualizar(manualId, payload);
        else savedManual = await apiManualSupportGuardar({ ...payload, images: images || [] });
        supportDebugLog("Editor: n8n confirmó guardado", {
          operation: wasUpdating ? "actualizar" : "crear",
          manualId: savedManual?.manual_id ?? savedManual?.manualId ?? manualId ?? null,
          response: savedManual,
        });
      } catch (apiError) {
        supportDebugLog("Editor: error de n8n; intentando respaldo local", {
          operation: wasUpdating ? "actualizar" : "crear",
          manualId,
          status: apiError.response?.status || apiError.httpCode || null,
          code: apiError.code || null,
          stack: apiError.stack,
          message: apiError.message,
          responseData: apiError.response?.data,
        }, "error");
        try {
          await persistirCapturaLocal();
          supportDebugLog("Editor: respaldo local completado tras error de n8n", { localDraftId });
          setNotice({ type: "warning", text: saveFailureMessage(apiError) });
        } catch (localError) {
          supportDebugLog("Editor: también falló el respaldo local", {
            message: localError.message,
            code: localError.code || null,
          }, "error");
          setNotice({ type: "error", text: `${errorMessage(apiError, "No se pudo guardar en el catálogo.")} Además, ${errorMessage(localError, "falló el guardado local.")}` });
        }
        return;
      }

      let savedManualId = savedManual?.manual_id ?? savedManual?.manualId ?? manualId;
      if (!savedManualId) {
        try {
          const currentCatalog = await apiManualesSupportList();
          savedManualId = (currentCatalog?.items || []).find(item => item.slug === form.slug)?.manual_id;
        } catch {
          // El guardado ya fue aceptado; no se debe presentar como fallido ni provocar un POST duplicado.
        }
      }
      let copiaLocalPendiente = false;
      if (localDraftId) {
        try {
          await deleteManualDraft(localDraftId);
        } catch {
          copiaLocalPendiente = true;
        }
      }
      if (savedManualId) setManualId(savedManualId);
      setLocalDraftId(null);
      setFiles([]);
      setCaptions([]);
      setFileInputKey(value => value + 1);
      if (savedManualId) {
        try {
          await cargarImagenesGuardadas(await apiManualSupportDetalle(savedManualId));
        } catch {
          limpiarImagenesGuardadas();
          setStoredImagesError(true);
        }
      } else {
        limpiarImagenesGuardadas();
      }
      cerrarPruebaConocimiento();
      await cargarCapturasLocales();
      setNotice({
        type: copiaLocalPendiente || !savedManualId ? "warning" : "success",
        text: copiaLocalPendiente
          ? "Manual guardado en el catálogo. La copia local sigue guardada y puedes eliminarla desde Capturas locales."
          : !savedManualId
            ? "El servicio aceptó el guardado, pero no devolvió el identificador. El manual ya se envió; actualizal catálogo antes de volver a guardarlo para evitar duplicados."
            : wasUpdating ? "Manual actualizado en el catálogo. Puedes probarlo con el agente antes de publicarlo."
              : "Manual guardado como borrador en el catálogo. Puedes probarlo con el agente antes de publicarlo.",
      });
      await cargarCatalogo();
      if (!savedManualId) limpiarEditor();
    } catch (error) {
      supportDebugLog("Editor: error durante la preparación o cierre del guardado", {
        message: error.message,
        code: error.code || null,
        stack: error.stack,
        status: error.response?.status || null,
        responseData: error.response?.data,
      }, "error");
      try {
        await persistirCapturaLocal();
        setNotice({
          type: "warning",
          text: `${errorMessage(error, "No se pudo preparar el manual para enviarlo.")} La captura y las imágenes originales quedaron guardadas en este navegador.`,
        });
      } catch (localError) {
        supportDebugLog("Editor: falló el respaldo local después del error de preparación", {
          message: localError.message,
          code: localError.code || null,
        }, "error");
        setNotice({
          type: "error",
          text: `${errorMessage(error, "No se pudo preparar el manual para enviarlo.")} Además, ${errorMessage(localError, "falló el guardado local.")}`,
        });
      }
    } finally {
      setPreparandoImagenes(false);
      setBusy(false);
    }
  };
  const editarManual = async (item) => {
    setBusy(true);
    setNotice(null);
    try {
      let detail = null;
      try {
        detail = await apiManualSupportDetalle(item.manual_id);
      } catch (detailError) {
        // Fallback si el workflow aún no expone GET detalle: usa la fila del catálogo.
        const status = detailError.response?.status;
        if ((status === 404 || status === 405) && item.slug) {
          detail = item;
          setNotice({ type: "warning", text: "No se pudo cargar el detalle completo; revisa el contenido antes de guardar." });
        } else {
          throw detailError;
        }
      }
      setForm({
        slug: detail.slug || "", title: detail.title || "", topic: detail.topic || "",
        version: detail.version || "1.0", content_markdown: detail.content_markdown || "",
      });
      setManualId(detail.manual_id);
      setLocalDraftId(null);
      setFiles([]);
      setCaptions([]);
      setFileInputKey(value => value + 1);
      await cargarImagenesGuardadas(detail);
      setNotice(current => current || { type: "info", text: "Editando un manual del catálogo. Las imágenes nuevas se agregarán después de las guardadas." });
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (error) {
      setNotice({ type: "error", text: errorMessage(error, "No se pudo cargar el manual para editarlo.") });
    } finally {
      setBusy(false);
    }
  };

  const eliminarManual = async (item) => {
    if (!window.confirm(`¿Eliminar definitivamente el manual «${item.title}» y sus imágenes?`)) return;
    setBusy(true);
    setNotice(null);
    try {
      await apiManualSupportEliminar(item.manual_id);
      setItems(previous => previous.filter(manual => String(manual.manual_id) !== String(item.manual_id)));
      if (manualId === item.manual_id) limpiarEditor();
      if (testTarget?.manual.manual_id === item.manual_id) {
        testPreviewUrls.current.forEach(preview => preview && URL.revokeObjectURL(preview));
        testPreviewUrls.current = [];
        setTestTarget(null);
      }
      setNotice({ type: "success", text: "Manual eliminado del catálogo." });
      await cargarCatalogo();
    } catch (error) {
      setNotice({ type: "error", text: errorMessage(error, "No se pudo eliminar el manual.") });
    } finally {
      setBusy(false);
    }
  };
  const enviarCapturaLocal = async (draft) => {
    const data = draft.form || {};
    const imagenes = draft.images || [];
    if (!data.title?.trim() || !data.topic?.trim() || !data.version?.trim() || !/^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(data.slug || "") || (data.content_markdown || "").trim().length < 30) {
      setNotice({ type: "error", text: "La captura tiene datos incompletos. Ábrela para completar los campos antes de enviarla." });
      return;
    }
    if (!Array.isArray(imagenes) || imagenes.some(image => !(image.blob instanceof Blob))) {
      setNotice({ type: "error", text: "No se pudieron recuperar todas las imágenes de esta captura. La copia local se conserva." });
      return;
    }
    if (draft.manual_id && imagenes.length) {
      setNotice({ type: "warning", text: "Abre esta captura en el editor para agregar sus imágenes sin perder las que ya están guardadas." });
      return;
    }
    setBusy(true);
    setNotice(null);
    try {
      if (!draft.manual_id) {
        const catalogo = await apiManualesSupportList();
        if (catalogo.items.some(item => item.slug === data.slug)) {
          setNotice({ type: "warning", text: "Ya existe un manual con ese slug en el catálogo. Revisal catálogo antes de enviar esta captura para evitar sobrescribirlo." });
          return;
        }
      }
      const archivos = imagenes.map(image => new File([image.blob], image.file_name, { type: image.media_type }));
      setPreparandoImagenes(Boolean(archivos.length));
      const images = archivos.length ? await prepararImagenesParaN8n(archivos, imagenes.map(image => image.caption || ""), data, Boolean(draft.manual_id)) : undefined;
      setPreparandoImagenes(false);
      const payload = { ...data, modified_by: "Support", ...(images !== undefined ? { images } : {}) };
      if (draft.manual_id) await apiManualSupportActualizar(draft.manual_id, payload);
      else await apiManualSupportGuardar({ ...payload, images: images || [] });
      setNotice({ type: "success", text: "Captura enviada al catálogo. La copia local se conserva; verifica el manual en el catálogo." });
      await cargarCatalogo();
    } catch (error) {
      setNotice({ type: "warning", text: `No se pudo confirmar el envío de la captura. La copia local se conserva. ${errorMessage(error, "Revisal catálogo antes de reintentar.")}` });
    } finally {
      setPreparandoImagenes(false);
      setBusy(false);
    }
  };

  const abrirCapturaLocal = async (draft) => {
    limpiarImagenesGuardadas();
    setForm({ ...empty, ...draft.form });
    setManualId(draft.manual_id || null);
    setFiles((draft.images || []).map(image => new File([image.blob], image.file_name, { type: image.media_type })));
    setCaptions((draft.images || []).map(image => image.caption || ""));
    setLocalDraftId(draft.draft_id);
    setFileInputKey(value => value + 1);
    setNotice({ type: "info", text: "Editando una captura local. Usa «Guardar captura local» para conservarla o envíala al catálogo cuando el servicio esté disponible." });
    if (draft.manual_id) {
      setBusy(true);
      try {
        await cargarImagenesGuardadas(await apiManualSupportDetalle(draft.manual_id));
      } catch {
        setStoredImagesError(true);
      } finally {
        setBusy(false);
      }
    }
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const eliminarCapturaLocal = async (draft) => {
    if (!window.confirm(`¿Eliminar la captura local «${draft.form.title}»?`)) return;
    try {
      await deleteManualDraft(draft.draft_id);
      if (localDraftId === draft.draft_id) limpiarEditor();
      await cargarCapturasLocales();
      setNotice({ type: "success", text: "Captura local eliminada." });
    } catch (error) {
      setNotice({ type: "error", text: errorMessage(error, "No se pudo eliminar la captura local.") });
    }
  };

  const publicar = async (id) => {
    setBusy(true);
    setNotice(null);
    try {
      await apiManualSupportPublicar(id);
      setNotice({ type: "success", text: "Manual publicado; ya está disponible para consulta." });
      await cargarCatalogo();
    } catch (error) {
      setNotice({ type: "error", text: errorMessage(error, "No se pudo publicar el manual.") });
    } finally {
      setBusy(false);
    }
  };

  const elegirArchivos = (event) => {
    const selected = Array.from(event.target.files || []);
    if (!selected.length) return;
    if ((manualId ? storedImageCount : 0) + files.length + selected.length > MAX_IMAGES || selected.some(file => !ALLOWED_IMAGE_TYPES.includes(file.type) || file.size > MAX_IMAGE_BYTES)) {
      setNotice({ type: "error", text: "Selecciona hasta 10 imágenes PNG, JPG, WEBP o GIF, de máximo 4 MB cada una." });
      event.target.value = "";
      return;
    }
    setFiles(current => [...current, ...selected]);
    setCaptions(current => [...current, ...selected.map(() => "")]);
    event.target.value = "";
    setNotice(null);
  };

  const actualizarCaption = (index, value) => {
    setCaptions(current => current.map((caption, itemIndex) => itemIndex === index ? value : caption));
  };

  return <Box sx={{ maxWidth: 1180, mx: "auto", pb: 4 }}>
    <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" alignItems={{ sm: "center" }} spacing={1} sx={{ mb: 1 }}>
      <Box>
        <Typography variant="h4" gutterBottom>Manuales de soporte</Typography>
        <Typography color="text.secondary">Redacta instrucciones por tema y agrega capturas de pantalla.</Typography>
      </Box>
      <Chip color={localDraftId ? "warning" : manualId ? "info" : "default"} label={localDraftId ? "Editando captura local" : manualId ? "Editando manual de Support" : "Editor de manuales"} />
    </Stack>

    <Alert severity="info" sx={{ my: 2 }}>Los manuales y sus imágenes se guardan en el catálogo. Si no se puede completar el envío, se conserva una copia en este navegador para reintentar.</Alert>
    {notice && <Alert severity={notice.type} sx={{ mb: 2 }} onClose={() => setNotice(null)}>{notice.text}</Alert>}

    <Card sx={{ mb: 3 }}>
      <CardContent component="form" ref={formRef} onSubmit={guardarEnCatalogo}>
        <Stack spacing={2}>
          <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" alignItems={{ sm: "center" }} spacing={1}>
            <Box>
              <Typography variant="h6">{manualId ? "Editar manual" : localDraftId ? "Editar captura local" : "Nuevo manual"}</Typography>
              <Typography variant="body2" color="text.secondary">Completa el contenido y envíalo al catálogo.</Typography>
            </Box>
            {(localDraftId || manualId) && <Button type="button" onClick={limpiarEditor}>Nuevo manual</Button>}
          </Stack>
          <TextField label="Título del manual" value={form.title} onChange={event => setForm({ ...form, title: event.target.value })} required inputProps={{ maxLength: 200 }} />
          <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
            <TextField label="Tema" value={form.topic} onChange={event => setForm({ ...form, topic: event.target.value })} required fullWidth inputProps={{ maxLength: 120 }} helperText="Ejemplo: Inventarios, ventas o configuración." />
            <TextField label="Slug" value={form.slug} onChange={event => setForm({ ...form, slug: event.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "-").replace(/-+/g, "-").replace(/^-|-$/g, "") })} required fullWidth disabled={Boolean(manualId)} inputProps={{ maxLength: 100, pattern: "[a-z0-9]+(-[a-z0-9]+)*" }} helperText={manualId ? "El slug identifica el registro y no se puede cambiar al editar (el guardado es por slug)." : "Identificador único: minúsculas, números y guiones."} />
            <TextField label="Versión" value={form.version} onChange={event => setForm({ ...form, version: event.target.value })} required sx={{ minWidth: 150 }} inputProps={{ maxLength: 40 }} />
          </Stack>
          <TextField label="Instrucciones en Markdown" value={form.content_markdown} onChange={event => setForm({ ...form, content_markdown: event.target.value })} required multiline minRows={12} inputProps={{ minLength: 30, maxLength: 500000 }} helperText={`${form.content_markdown.length.toLocaleString()} / 500,000 caracteres. Admite títulos, listas, pasos numerados, enlaces y formato Markdown.`} />
          {manualId && <Box>
            <Typography variant="subtitle1">Imágenes guardadas en el manual</Typography>
            {storedImagesLoading ? <CircularProgress size={24} /> : storedImagesError ? <Alert severity="warning">No se pudo consultar la lista de imágenes guardadas. Vuelve a abrir el manual para comprobarlas.</Alert> : <>
              {storedImageCount === 0 && <Typography variant="body2" color="text.secondary">Este manual no tiene imágenes guardadas.</Typography>}
              {(storedImageCount > storedImages.filter(image => image.url).length) && <Alert severity="warning" sx={{ mt: 1 }}>No se pudieron cargar todas las imágenes guardadas. Los datos del manual permanecen en el servidor.</Alert>}
              {!!storedImages.length && <Stack direction="row" spacing={1} sx={{ mt: 1, flexWrap: "wrap", rowGap: 1 }}>
                {storedImages.map((image, index) => image.url && <Box key={image.image_id} sx={{ maxWidth: 180 }}>
                  <Box component="a" href={image.url} target="_blank" rel="noopener noreferrer" aria-label={`Abrir ${image.caption || image.file_name || `imagen ${index + 1}`}`}>
                    <Box component="img" src={image.url} alt={image.caption || image.file_name || `Imagen ${index + 1} del manual`} sx={{ width: 160, height: 110, objectFit: "contain", border: "1px solid", borderColor: "divider", borderRadius: 1 }} />
                  </Box>
                  <Typography variant="caption" display="block">{index + 1}. {image.caption || image.file_name || `Imagen ${index + 1}`}</Typography>
                </Box>)}
              </Stack>}
            </>}
          </Box>}
          <Stack direction={{ xs: "column", sm: "row" }} alignItems={{ sm: "center" }} spacing={1}>
            <Button component="label" variant="outlined" disabled={busy || storedImagesLoading}>Agregar imágenes<input key={fileInputKey} hidden type="file" multiple accept={ALLOWED_IMAGE_TYPES.join(",")} onChange={elegirArchivos} /></Button>
            <Typography variant="body2" color="text.secondary">{(manualId ? storedImageCount : 0) + files.length} de {MAX_IMAGES} imágenes · máximo 4 MB cada una. Las imágenes se guardan en el orden mostrado.</Typography>
          </Stack>
          <Typography variant="body2" color="text.secondary">Para relacionar las capturas con las preguntas, usa nombres descriptivos separados por guiones, por ejemplo: recuperar-contrasena.png, y agrega una descripción del paso.</Typography>
          {manualId && storedImageCount > 0 && files.length > 0 && <Alert severity="info">Las imágenes nuevas se agregarán después de las guardadas.</Alert>}
          {!!selectedPreviews.length && <Stack spacing={1}>{selectedPreviews.map((preview, index) => <Card variant="outlined" key={preview.url}>
            <CardContent sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "150px minmax(0, 1fr)" }, gap: 2, alignItems: "center", p: 1.5, "&:last-child": { pb: 1.5 } }}>
              <Box component="img" src={preview.url} alt={captions[index] || preview.name} sx={{ width: "100%", height: 105, objectFit: "contain", border: "1px solid", borderColor: "divider", borderRadius: 1 }} />
              <Stack spacing={1}>
                <Typography variant="body2" fontWeight={600}>{(manualId ? storedImageCount : 0) + index + 1}. {preview.name} · {(preview.size / 1024 / 1024).toFixed(2)} MB</Typography>
                <TextField label={`Descripción de imagen ${index + 1}`} value={captions[index] || ""} onChange={event => actualizarCaption(index, event.target.value)} size="small" inputProps={{ maxLength: 500 }} helperText="Describe el tema o paso; por ejemplo: Ingreso de usuario y contraseña." />
              </Stack>
            </CardContent>
          </Card>)}</Stack>}
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
            <Button type="button" variant="outlined" disabled={busy} onClick={guardarCapturaLocal}>{busy ? "Guardando…" : "Guardar respaldo local"}</Button>
            <Button type="submit" variant="contained" disabled={busy}>{busy ? preparandoImagenes ? "Optimizando imágenes…" : "Guardando…" : manualId ? "Actualizar en el catálogo" : "Guardar en el catálogo"}</Button>
            {manualId && <Button type="button" variant="outlined" disabled={busy || testPreparing} onClick={() => abrirPruebaConocimiento(manualId)}>{testPreparing ? "Preparando prueba…" : "Probar conocimiento"}</Button>}
          </Stack>
        </Stack>
      </CardContent>
    </Card>

    {testTarget && <AgentKnowledgeTestChat
      key={testTarget.sessionKey}
      manual={testTarget.manual}
      images={testTarget.images}
      onStartJob={apiManualSupportConocimientoIniciar}
      onGetJob={apiManualSupportConocimientoEstado}
      onCloseSession={apiManualSupportCerrarConversacion}
      onClose={cerrarPruebaConocimiento}
    />}

    <Card sx={{ mb: 3 }}>
      <CardContent>
        <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" alignItems={{ sm: "center" }} spacing={1} sx={{ mb: 1 }}>
          <Box>
            <Typography variant="h6">Capturas locales</Typography>
            <Typography variant="body2" color="text.secondary">Se conservan en este navegador. Puedes enviarlas individualmente al catálogo; si estás editando una captura, guárdala desde el editor.</Typography>
          </Box>
          <Tooltip title="Actualizar capturas locales"><span><IconButton aria-label="Actualizar capturas locales" onClick={cargarCapturasLocales} disabled={draftsLoading}><RefreshIcon /></IconButton></span></Tooltip>
        </Stack>
        {draftsLoading ? <CircularProgress size={24} /> : localDrafts.length ? <Stack spacing={1}>{localDrafts.map(draft => <Card variant="outlined" key={draft.draft_id}>
          <CardContent sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap", p: 1.5, "&:last-child": { pb: 1.5 } }}>
            <Box sx={{ flexGrow: 1, minWidth: 220 }}><Typography fontWeight={700}>{draft.form.title || "Sin título"}</Typography><Typography variant="body2" color="text.secondary">{draft.form.topic || "Sin tema"} · {draft.form.slug || "Sin slug"} · v{draft.form.version} · {(draft.images || []).length} imagen(es)</Typography><Typography variant="caption" color="text.secondary">Actualizada {new Date(draft.updated_at).toLocaleString()}</Typography></Box>
            <Chip size="small" color="warning" label="Solo este navegador" />
            <Button type="button" variant="outlined" disabled={busy || localDraftId === draft.draft_id} onClick={() => enviarCapturaLocal(draft)}>Enviar al catálogo</Button>
            <Tooltip title="Editar captura"><IconButton aria-label={`Editar ${draft.form.title}`} onClick={() => abrirCapturaLocal(draft)} disabled={busy}><EditOutlinedIcon /></IconButton></Tooltip>
            <Tooltip title="Eliminar captura"><IconButton aria-label={`Eliminar ${draft.form.title}`} onClick={() => eliminarCapturaLocal(draft)} disabled={busy}><DeleteOutlineIcon /></IconButton></Tooltip>
          </CardContent>
        </Card>)}</Stack> : <Typography color="text.secondary">Todavía no hay capturas locales.</Typography>}
      </CardContent>
    </Card>

    <Stack spacing={1}>
      <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" alignItems={{ sm: "center" }} spacing={1}>
          <Box><Typography variant="h6">Manuales almacenados en el catálogo</Typography><Typography variant="body2" color="text.secondary">Los borradores se conservan en el catálogo y puedes probarlos antes de publicarlos.</Typography></Box>
        <Button startIcon={<RefreshIcon />} onClick={cargarCatalogo} disabled={loading}>Actualizar catálogo</Button>
      </Stack>
      {catalogError && <Alert severity="warning">No se pudo consultar el catálogo todavía: {catalogError}. Puedes continuar guardando capturas locales.</Alert>}
      {loading ? <CircularProgress /> : items.map(item => <Card key={item.manual_id}><CardContent sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
        <Box sx={{ flexGrow: 1 }}><Typography>{item.title}</Typography><Typography variant="body2" color="text.secondary">{item.topic} · {item.slug} · v{item.version} · {item.image_count} imagen(es)</Typography>
          {!!item.imagePreviews?.length && <Stack direction="row" spacing={1} sx={{ mt: 1 }}>{item.imagePreviews.map((url, index) => url ? <Box key={`${item.manual_id}-${index}`} component="img" src={url} alt={`Imagen ${index + 1} del manual ${item.title}`} sx={{ width: 110, height: 78, objectFit: "contain", border: "1px solid", borderColor: "divider", borderRadius: 1 }} /> : <Typography key={`${item.manual_id}-${index}`} variant="caption" color="error">No se pudo cargar la vista previa {index + 1}</Typography>)}</Stack>}
        </Box>
        <Chip label={item.is_published ? "Publicado" : "Borrador"} color={item.is_published ? "success" : "default"} />
        <Tooltip title="Editar manual"><span><IconButton aria-label={`Editar ${item.title}`} disabled={busy} onClick={() => editarManual(item)}><EditOutlinedIcon /></IconButton></span></Tooltip>
        <Tooltip title="Eliminar manual"><span><IconButton aria-label={`Eliminar ${item.title}`} disabled={busy} onClick={() => eliminarManual(item)}><DeleteOutlineIcon /></IconButton></span></Tooltip>
        <Button variant="outlined" disabled={busy || testPreparing} onClick={() => abrirPruebaConocimiento(item)}>{testPreparing ? "Preparando prueba…" : "Probar conocimiento"}</Button>
        {!item.is_published && <Button disabled={busy} onClick={() => publicar(item.manual_id)}>Publicar</Button>}
      </CardContent></Card>)}
      {!loading && !catalogError && !items.length && <Typography color="text.secondary">El catálogo está vacío.</Typography>}
    </Stack>
  </Box>;
}
